import { createSlice } from "@reduxjs/toolkit";
import { ILogin, IProfile } from "../interfaces/IProfile";
import { t } from "i18next";
import {
  fetchProfileData,
  registerBMCProfileApi,
  updateBMCProfileApi,
  validateOTPTokenAPI,
  generateOTPTokenAPI,
  loginApi,
  restPasswordApi,
  forgetPasswordApi,
  validateCompanyNameUniqueApi,
  validateEmailUniqueApi,
  validatePhoneNumberUniqueApi,
  sendRequestToOwnerApi,
} from "src/api/bmcApi";
import Cookies from "js-cookie";
import {
  AUTH_TOKEN,
  devLocationOrigin,
  IS_CIVIL_DEFENSE,
} from "src/resources/constant";
import cookies from "js-cookie";
import { toast } from "react-toastify";

import { handleResponse } from "src/services/apiServices";
import { encryptPassword, toPascal, toCamel } from "src/utils/helpers";
import { trackPromise } from "react-promise-tracker";

export const getBMCProfile = () => {
  return async (dispatch: any, getState: any) => {
    const token = Cookies.get(AUTH_TOKEN);
    if (token || window.location.origin === devLocationOrigin) {
      await trackPromise(
        fetchProfileData()
          .then((res) => {
            const responseInCamelCase = toCamel(res.data.Profile);
            dispatch(
              setProfileData({
                ...responseInCamelCase,
                lon: responseInCamelCase?.lon?.toString(),
                lat: responseInCamelCase?.lat?.toString(),
              })
            );
            if (window.location.origin === devLocationOrigin)
              dispatch(setToken("local") as any); //in dev mode
          })
          .catch((err) => {
            if (window.location.origin === devLocationOrigin)
              dispatch(setToken("local") as any); //in dev mode
            // dispatch(setToken("test") as any); //in dev mode
          })
      );
    }
  };
};
export const updateBMCProfile = (onSuccess: any) => {
  return async (dispatch: any, getState: any) => {
    const { bMCProfile } = getState();
    const language = getState().lookup.language;
    const data = toPascal(bMCProfile.BMCData);
    await trackPromise(
      updateBMCProfileApi(data)
        .then((res) => {
          handleResponse(res, language);
          onSuccess?.();
        })
        .catch((err) => {
          handleResponse(err, language);
        })
    );
  };
};
export const createBMCProfile = (onSuccess: any, onFailure: any) => {
  return async (dispatch: any, getState: any) => {
    const { bMCProfile } = getState();
    const language = getState().lookup.language;
    const data = toPascal(bMCProfile.BMCData);
    return await trackPromise(
      registerBMCProfileApi(data)
        .then((res) => {
          if (handleResponse(res, language)) {
            const token = Cookies.get(AUTH_TOKEN);
            Cookies.remove(AUTH_TOKEN);
            if (token) dispatch(setToken(token) as any);
            onSuccess?.();
          }
        })
        .catch((err) => {
          onFailure?.(err);
          handleResponse(err, language);
        })
    );
  };
};

export const validateCompanyNameUnique = (newCompanyName: string) => {
  return async (dispatch: any, getState: any) => {
    const { bMCProfile } = getState();

    const data = {
      CompanyName: newCompanyName ? newCompanyName : null,
      Id: bMCProfile.BMCData.id ? bMCProfile.BMCData.id : null,
    };

    const language = getState().lookup.language;
    return await trackPromise(
      validateCompanyNameUniqueApi(data)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          handleResponse(err, language);
        })
    );
  };
};
export const validateEmailUnique = (newEmail?: string) => {
  return async (dispatch: any, getState: any) => {
    const { bMCProfile } = getState();

    const data = {
      EmailAddress: newEmail ? newEmail : null,
      Id: bMCProfile.BMCData.id ? bMCProfile.BMCData.id : null,
    };

    const language = getState().lookup.language;
    return await trackPromise(
      validateEmailUniqueApi(data)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          handleResponse(err, language);
        })
    );
  };
};
export const validatePhoneNumberUnique = (newPhonNumber: string) => {
  return async (dispatch: any, getState: any) => {
    const { bMCProfile } = getState();

    const data = {
      PhoneNumber: newPhonNumber ? newPhonNumber : null,
      Id: bMCProfile.BMCData.id ? bMCProfile.BMCData.id : null,
    };

    const language = getState().lookup.language;

    return await trackPromise(
      validatePhoneNumberUniqueApi(data)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          handleResponse(err, language);
        })
    );
  };
};

export const verifyOTP = (IdentifierKey: string, hideOTP: any) => {
  return async (dispatch: any, getState: any) => {
    const { bMCProfile } = getState();
    const OTPObj = {
      OTPCode: bMCProfile.otp,
      IdentifierKey: IdentifierKey,
    };
    const language = getState().lookup.language;

    // returning from this API we eill see if it is validated  or not
    return await trackPromise(
      validateOTPTokenAPI(OTPObj)
        .then((res) => {
          const isValid = handleResponse(res, language);
          if (
            !isValid &&
            res.data.ErrorCode == 16 //this mean that the "OTP Expired"
          ) {
            hideOTP(true);
          }
          return isValid;
        })
        .catch((err) => {
          handleResponse(err, language);
        })
    );
  };
};
export const generateOTP = (
  otpTyp: number,
  IdentifierKey: string,
  isForgetPassword = false
) => {
  return async (dispatch: any, getState: any) => {
    const OTPObj = {
      IdentifierKey: IdentifierKey,
      VerificationType: otpTyp,
      IsForgetPassword: isForgetPassword,
    };
    return await generateOTPTokenAPI(OTPObj);
  };
};

export const loginUser = (onSuccess: Function) => {
  return async (dispatch: any, getState: any) => {
    const { bMCProfile } = getState();
    const language = getState().lookup.language;
    const loginData: ILogin = {
      Username: bMCProfile.userName,
      Password: encryptPassword(bMCProfile.password),
      //Password: bMCProfile.password,
    };
    await trackPromise(
      loginApi(loginData)
        .then((res) => {
          if (!res) {
            toast.error(t("loginError")); //"Either username or password is incorrect");
          } else if (handleResponse(res, language)) {
            cookies.set(IS_CIVIL_DEFENSE, res.data?.IsCivilDefense);
            dispatch(getBMCProfile() as any);
            const token = cookies.get(AUTH_TOKEN);
            if (token) {
              dispatch(setToken(token) as any);
            }
            onSuccess?.(res.data);
          }
        })
        .catch((error: any) => {
          if (!error || error.response?.status === 401) {
            toast.error(t("loginError")); //"Either username or password is incorrect");
          } else {
            handleResponse(error);
          }
        })
    );
  };
};

export const forgetPassword = (onSuccess: Function) => {
  return async (dispatch: any, getState: any) => {
    const { bMCProfile } = getState();
    const email = bMCProfile.BMCData?.email;
    const language = getState().lookup.language;
    return await trackPromise(
      forgetPasswordApi(email)
        .then((res) => {
          if (handleResponse(res, language)) onSuccess?.();
        })
        .catch((err) => {
          handleResponse(err, language);
        })
    );
  };
};
export const restPassword = (onSuccess: any) => {
  return async (dispatch: any, getState: any) => {
    const { bMCProfile } = getState();
    const language = getState().lookup.language;
    const restPasswordData = {
      NewPassword: encryptPassword(bMCProfile.newPassword),
      OldPassword: encryptPassword(bMCProfile.password),
    };
    return await trackPromise(
      restPasswordApi(restPasswordData)
        .then((res) => {
          if (handleResponse(res, language)) onSuccess?.(res);
        })
        .catch((err) => {
          handleResponse(err, language);
        })
    );
  };
};

export const sendRequestToOwner = (payload: any) => {
  return async (dispatch: any, getState: any) => {
    return await trackPromise(
      sendRequestToOwnerApi(toPascal(payload))
        .then((response: any) => {})
        .catch((err) => {
          handleResponse(err);
        })
    );
  };
};

const initialState: IProfile = {
  BMCData: {
    id: "",
    name: "",
    tradeLicenseNumber: "",
    tradeLicenseExpiry: "",
    civilDefenseLicense: "",
    civilDefenseLicenseExpiry: "",
    categoryOfLicense: "",
    emirate: "bbfaefd1-1f21-e811-910c-00505686c522", //"Abu Dhabi"
    street: "",
    city: "",
    lon: "",
    lat: "",
    officeAddress: "",
    officePhoneNumber: "",
    contactPersonName: "",
    designation: "",
    phoneNumber: "",
    email: "",
    numberOfTechnician: 0,
    numberOfElectricalEngineer: 0,
    numberOfCertifiedEngineers: 0,
    numberOfMechanicEngineer: 0,
    numberofElectricalTechnician: 0,
    numberOfMechanicalTechnician: 0,
    cDApprovedLicense: {
      name: "",
      fileExtension: "",
      fileBase64: "",
      subject: "",
    },
    cityArabicName: "",
    cityEnglishName: "",
  },
  isEditMode: false,
  isEditProfilePopupOpen: false,
  isOTPPopupOpen: false,
  isEmailVerified: false,
  isSmSVerified: false,
  otp: "",
  token: "",
  userName: "",
  password: "",
  newPassword: "",
  confirmNewPassword: "",
  sendRequestToOwnerPopup: {
    sendRequestToOwnerData: {
      requestReferenceNumber: "",
      systemServiceId: "",
      serviceTerritoryId: "",
      propertyId: "",
    },
  },
};

const bMCProfile = createSlice({
  name: "bmcProfile",
  initialState,
  reducers: {
    setProfileData: (state, action) => {
      state.BMCData = action.payload;
    },
    setIsEditMode: (state, action) => {
      state.isEditMode = action.payload;
    },
    setIsEditProfilePopupOpen: (state, action) => {
      state.isEditProfilePopupOpen = action.payload;
    },
    resetBMCProfileData: (state) => {
      state.BMCData = initialState.BMCData;
    },
    setOTPPopupOpen: (state, action) => {
      state.isOTPPopupOpen = action.payload;
    },
    setIsEmailVerified: (state, action) => {
      state.isEmailVerified = action.payload;
    },
    setIsSmSVerified: (state, action) => {
      state.isSmSVerified = action.payload;
    },
    setOTP: (state, action) => {
      state.otp = action.payload;
    },
    setUserName: (state, action) => {
      state.userName = action.payload;
    },
    setPassword: (state, action) => {
      state.password = action.payload;
    },
    setNewPassword: (state, action) => {
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
    },
    setEmail: (state, action) => {
      state.BMCData.email = action.payload;
    },
    setToken: (state, action) => {
      if (action.payload && window.location.origin === devLocationOrigin)
        state.token = "local";
      else state.token = action.payload;
    },
    initializeSendRequestToOwnerPopupData: (state, action) => {
      state.sendRequestToOwnerPopup.sendRequestToOwnerData = {
        ...action.payload,
      };
    },
    restSendRequestToOwnerPopup: (state) => {
      state.sendRequestToOwnerPopup = initialState.sendRequestToOwnerPopup;
    },
  },
});

export const {
  setProfileData,
  setIsEditMode,
  setIsEditProfilePopupOpen,
  resetBMCProfileData,
  setOTPPopupOpen,
  setIsEmailVerified,
  setIsSmSVerified,
  setOTP,
  setEmail,
  setUserName,
  setPassword,
  setNewPassword,
  setToken,
  restSendRequestToOwnerPopup,
  initializeSendRequestToOwnerPopupData,
} = bMCProfile.actions;
export default bMCProfile.reducer;
